import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { normalizedPurchaseOrders, promotePurchaseOrder, purchaseOrderError, PurchaseOrders, PurchaseOrderSnapshot, samePurchaseOrders } from './purchase-orders.model';

@Component({
  selector: 'app-purchase-order-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './purchase-order-editor.component.html',
  styleUrls: ['./purchase-order-editor.component.scss'],
})
export class PurchaseOrderEditorComponent implements OnChanges, OnDestroy {
  @Input() ladingUuid: string | null = null;
  @Input() disabled = false;
  @Output() saved = new EventEmitter<PurchaseOrderSnapshot>();
  snapshot: PurchaseOrderSnapshot | null = null;
  draft: PurchaseOrders = { primary: '', additional: [] };
  newPo = '';
  loading = false;
  saving = false;
  error = '';
  message = '';
  private loadedUuid: string | null = null;
  private generation = 0;
  private destroyed = false;
  private pending: { operation_uuid: string; lading_uuid: string; revision: string; primary: string; additional: string[] } | null = null;
  private readonly endpoint = '/api/work_orders/purchase_orders.php';

  constructor(private readonly http: HttpClient) {}
  ngOnChanges(): void {
    if (this.ladingUuid !== this.loadedUuid) {
      this.loadedUuid = this.ladingUuid;
      this.snapshot = null;
      this.pending = null;
      void this.reload(false);
    }
  }
  ngOnDestroy(): void { this.destroyed = true; this.generation++; }
  get hasChanges(): boolean { return !!this.snapshot && (!samePurchaseOrders(this.draft, this.snapshot) || !!this.newPo); }
  get validation(): string { return purchaseOrderError(this.draft); }
  get readonly(): boolean { return this.disabled || this.saving || !this.snapshot?.can_edit; }
  canLeave(): boolean { return !this.saving && (!this.hasChanges || window.confirm('Discard unsaved PO changes?')); }
  @HostListener('window:beforeunload', ['$event']) beforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasChanges || this.saving) { event.preventDefault(); event.returnValue = ''; }
  }
  trackRow(index: number): number { return index; }
  add(): void {
    if (this.readonly || !this.newPo.trim()) return;
    this.draft.additional.push(this.newPo.trim());this.newPo = '';this.error = '';this.message = '';
  }
  promote(index: number): void { if (!this.readonly) this.draft = promotePurchaseOrder(this.draft, index); }
  remove(index: number): void { if (!this.readonly) this.draft.additional.splice(index, 1); }
  private accept(snapshot: PurchaseOrderSnapshot): void {
    if (snapshot?.lading_uuid !== this.ladingUuid || !Array.isArray(snapshot.additional)) throw new Error('Unexpected PO response. Reload this order.');
    this.snapshot = snapshot;
    this.draft = { primary: snapshot.primary, additional: [...snapshot.additional] };
    this.newPo = '';this.pending = null;
  }
  async reload(confirm = true): Promise<void> {
    if (!this.ladingUuid || this.saving || (confirm && !this.canLeave())) return;
    const generation = ++this.generation;this.loading = true;this.error = '';this.message = '';
    try {
      const response = await firstValueFrom(this.http.get<{ success: boolean; data: PurchaseOrderSnapshot }>(this.endpoint, { params: { lading_uuid: this.ladingUuid } }));
      if (generation !== this.generation || this.destroyed) return;
      if (!response.success) throw new Error('Unable to load POs.');
      this.accept(response.data);
    } catch { if (generation === this.generation && !this.destroyed) this.error = 'Unable to load POs. Your changes have not been discarded.'; }
    finally { if (generation === this.generation && !this.destroyed) this.loading = false; }
  }
  async save(): Promise<void> {
    if (this.readonly || !this.snapshot) return;
    if (this.newPo.trim()) this.add();
    if (this.validation) { this.error = this.validation;return; }
    const desired = normalizedPurchaseOrders(this.draft);
    if (!this.hasChanges) return;
    // An uncertain request retains its operation ID for an identical retry.
    if (!this.pending || !samePurchaseOrders(this.pending, desired) || this.pending.revision !== this.snapshot.revision) {
      this.pending = { operation_uuid: crypto.randomUUID(), lading_uuid: this.snapshot.lading_uuid, revision: this.snapshot.revision, ...desired };
    }
    const request = this.pending;this.saving = true;this.error = '';this.message = '';
    try {
      const response = await firstValueFrom(this.http.put<{ success: boolean; data: PurchaseOrderSnapshot }>(this.endpoint, request));
      if (this.destroyed) return;
      if (!response.success || !samePurchaseOrders(response.data, desired)) throw new Error('unconfirmed_save');
      this.accept(response.data);
      this.message = response.data.integration.po_sync_enabled ? 'Saved in AMS. See integration status below for UFOs progress.' : 'Saved in AMS. UFOs PO sync is paused.';
      this.saved.emit(response.data);
    } catch (error: any) {
      if (this.destroyed) return;
      const code = error?.error?.error;
      this.error = code === 'purchase_orders_changed_reload' ? 'These POs changed since you opened them. Your draft is retained; reload to review the current values.'
        : code?.startsWith('protected_') ? 'This order or a split leg is delivered, invoiced or protected. PO changes were not saved.'
        : 'The save could not be confirmed. Your draft is retained. Retry the same save, or reload to check the current values.';
    } finally { this.saving = false; }
  }
  fieldLabel(field: string): string {
    const labels: Record<string, string> = { purchase_orders: 'Purchase orders', pro_bill: 'PRO', bol: 'BOL', load_weight: 'Billing weight', load_weight_actual: 'Actual weight', shipper_stop: 'Shipper', consignee_stop: 'Consignee', customer_assignment: 'Customer', carrier_assignment: 'Carrier', handling_quantity: 'Quantity and unit', pickup_schedule: 'Pickup schedule', delivery_schedule: 'Delivery schedule' };
    return labels[field] || field.replace(/_/g, ' ');
  }
}
